class CosmoCuratedSelection {
  constructor(section) {
    if (section.dataset.selectionReady === 'true') return;

    section.dataset.selectionReady = 'true';
    this.section = section;
    this.rail = section.querySelector('.cosmo-selection__grid');
    this.progress = section.querySelector('[data-selection-progress]');
    this.prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.progressFrameRequested = false;
    this.motionFrameRequested = false;
    this.isVisible = false;

    this.updateProgress = this.updateProgress.bind(this);
    this.requestProgressUpdate = this.requestProgressUpdate.bind(this);
    this.updateMotion = this.updateMotion.bind(this);
    this.requestMotionUpdate = this.requestMotionUpdate.bind(this);

    if (section.dataset.motion !== 'true' || this.prefersReducedMotion.matches) {
      section.classList.add('is-visible');
    } else {
      this.observer = new IntersectionObserver(
        ([entry]) => {
          if (!entry.isIntersecting) return;
          section.classList.add('is-visible');
          this.observer.disconnect();
        },
        { threshold: 0.08, rootMargin: '0px 0px -7% 0px' }
      );

      this.observer.observe(section);

      this.visibilityObserver = new IntersectionObserver(
        ([entry]) => {
          this.isVisible = entry.isIntersecting;
          if (this.isVisible) this.requestMotionUpdate();
        },
        { rootMargin: '20% 0px' }
      );

      this.visibilityObserver.observe(section);
      window.addEventListener('scroll', this.requestMotionUpdate, { passive: true });
      window.addEventListener('resize', this.requestMotionUpdate, { passive: true });
    }

    if (this.rail && this.progress) {
      this.rail.addEventListener('scroll', this.requestProgressUpdate, { passive: true });
      window.addEventListener('resize', this.requestProgressUpdate, { passive: true });
      this.updateProgress();
    }
  }

  requestProgressUpdate() {
    if (this.progressFrameRequested) return;

    this.progressFrameRequested = true;
    window.requestAnimationFrame(this.updateProgress);
  }

  updateProgress() {
    const maximum = Math.max(1, this.rail.scrollWidth - this.rail.clientWidth);
    const visibleRatio = Math.min(1, this.rail.clientWidth / this.rail.scrollWidth);
    const travelRatio = this.rail.scrollLeft / maximum;
    const progressScale = Math.max(visibleRatio, visibleRatio + travelRatio * (1 - visibleRatio));

    this.progress.style.setProperty('--selection-progress', progressScale.toFixed(3));
    this.progressFrameRequested = false;
  }

  requestMotionUpdate() {
    if (!this.isVisible || this.motionFrameRequested) return;

    this.motionFrameRequested = true;
    window.requestAnimationFrame(this.updateMotion);
  }

  updateMotion() {
    const bounds = this.section.getBoundingClientRect();
    const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
    const progress = (viewportHeight - bounds.top) / (viewportHeight + bounds.height);
    const centered = Math.max(-0.5, Math.min(0.5, progress - 0.5));
    const drift = centered * -32;

    this.section.style.setProperty('--selection-heading-drift', `${drift.toFixed(2)}px`);
    this.motionFrameRequested = false;
  }

  destroy() {
    this.observer?.disconnect();
    this.visibilityObserver?.disconnect();
    this.rail?.removeEventListener('scroll', this.requestProgressUpdate);
    window.removeEventListener('resize', this.requestProgressUpdate);
    window.removeEventListener('scroll', this.requestMotionUpdate);
    window.removeEventListener('resize', this.requestMotionUpdate);
  }
}

const initializeCosmoSelections = (root = document) => {
  root.querySelectorAll('[data-cosmo-selection]').forEach((section) => new CosmoCuratedSelection(section));
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => initializeCosmoSelections(), { once: true });
} else {
  initializeCosmoSelections();
}

document.addEventListener('shopify:section:load', (event) => initializeCosmoSelections(event.target));
